import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

export default function AdminPricingPlansPage() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({
    name: "",
    monthly: 0,
    yearly: 0,
    desc: "",
    features: "",
    cta: "",
    highlight: false,
    order: 0,
    isPublished: true,
  });

  async function load() {
    setError("");
    const res = await adminService.listPricingPlans();
    if (!res.ok) return setError(res.error || "Failed to load pricing plans");
    setItems(res.data?.items || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function featuresToList(s) {
    return String(s || "")
      .split(/\r?\n|,/)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Pricing Plans" highlight="content." description="Manage public pricing plans." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? (
            <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card>
          ) : null}

          <div><Button onClick={() => { setDraft({ name: "", monthly: 0, yearly: 0, desc: "", features: "", cta: "", highlight: false, order: 0, isPublished: true }); setAddOpen(true); }}>Add Plan</Button></div>

          <div className="grid md:grid-cols-2 gap-6">
            {items.map((p) => (
              <Card key={p._id} className="shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-3">
                    <span>{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.highlight ? "Most popular" : ""}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-3">
                  <div>monthly: {p.monthly} / yearly: {p.yearly}</div>
                  <div>{p.desc}</div>
                  <div className="text-xs">features: {(p.features || []).slice(0, 3).join(", ")}{(p.features || []).length > 3 ? "…" : ""}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(p); setDraft({ name: p.name, monthly: p.monthly, yearly: p.yearly, desc: p.desc, features: (p.features || []).join("\n"), cta: p.cta, highlight: Boolean(p.highlight), order: p.order || 0, isPublished: p.isPublished !== false }); setEditOpen(true); }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      const r = await adminService.deletePricingPlan(p._id);
                      if (!r.ok) return toast.error(r.error || "Delete failed");
                      toast.success("Plan deleted");
                      await load();
                    }}>Delete</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageSection>

      <FormModal open={addOpen} title="Add Pricing Plan" onClose={() => setAddOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Plan name" value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" type="number" placeholder="Monthly" value={draft.monthly} onChange={(e) => setDraft((p) => ({ ...p, monthly: Number(e.target.value) }))} />
            <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" type="number" placeholder="Yearly" value={draft.yearly} onChange={(e) => setDraft((p) => ({ ...p, yearly: Number(e.target.value) }))} />
          </div>
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Features (one per line)" value={draft.features} onChange={(e) => setDraft((p) => ({ ...p, features: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="CTA text" value={draft.cta} onChange={(e) => setDraft((p) => ({ ...p, cta: e.target.value }))} />
          <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={draft.highlight} onChange={(e) => setDraft((p) => ({ ...p, highlight: e.target.checked }))} /> Highlight</label>
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const r = await adminService.createPricingPlan({ ...draft, features: featuresToList(draft.features) });
              if (!r.ok) return toast.error(r.error || "Create failed");
              toast.success("Plan created");
              setAddOpen(false);
              await load();
            }}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={editOpen} title="Edit Pricing Plan" onClose={() => setEditOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Plan name" value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" type="number" placeholder="Monthly" value={draft.monthly} onChange={(e) => setDraft((p) => ({ ...p, monthly: Number(e.target.value) }))} />
            <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" type="number" placeholder="Yearly" value={draft.yearly} onChange={(e) => setDraft((p) => ({ ...p, yearly: Number(e.target.value) }))} />
          </div>
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Features (one per line)" value={draft.features} onChange={(e) => setDraft((p) => ({ ...p, features: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="CTA text" value={draft.cta} onChange={(e) => setDraft((p) => ({ ...p, cta: e.target.value }))} />
          <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={draft.highlight} onChange={(e) => setDraft((p) => ({ ...p, highlight: e.target.checked }))} /> Highlight</label>
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              if (!editing?._id) return;
              const r = await adminService.updatePricingPlan(editing._id, { ...draft, features: featuresToList(draft.features) });
              if (!r.ok) return toast.error(r.error || "Update failed");
              toast.success("Plan updated");
              setEditOpen(false);
              await load();
            }}>Update</Button>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>
    </PageShell>
  );
}

